import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PhoneInput, { type Country } from 'react-phone-number-input';
import { PhoneCountrySelect } from './PhoneCountrySelect';

export interface PhoneFieldProps {
    name: string;
    label?: string;
    required?: boolean;
    /** Pre-selected country (ISO code) for an empty field. Defaults to Norway. */
    defaultCountry?: Country;
    helpText?: string;
}

/**
 * Single international phone field — a flag country picker plus an auto-formatted
 * national number — bound to react-hook-form. The bound value is an E.164 string,
 * e.g. "+4798765432". Validate it with vPhone (rejects malformed numbers client-
 * side, before they reach Authy), and split it into the separate CountryCode /
 * PhoneNumber the backend expects with splitPhone() from lib/phone.
 *
 * The number input carries Bootstrap's .form-control class, so it inherits the
 * theme + colour mode for free; the country <select> is the library's native
 * one (OS-rendered dropdown). See .dkn-phone in styles/app.scss for the layout.
 */
export function PhoneField({ name, label, required, defaultCountry = 'NO', helpText }: PhoneFieldProps) {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const { t } = useTranslation();
    const error = errors[name];
    const message = typeof error?.message === 'string' ? t(error.message) : undefined;
    const errorId = `${name}-error`;
    const helpId = `${name}-help`;
    const describedBy = error ? errorId : helpText ? helpId : undefined;

    return (
        <Form.Group className="mb-3" controlId={name}>
            {label && (
                <Form.Label htmlFor={name}>
                    {label}
                    {required && (
                        <span className="field-required-mark" aria-hidden="true">
                            *
                        </span>
                    )}
                </Form.Label>
            )}
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <PhoneInput
                        defaultCountry={defaultCountry}
                        value={field.value || undefined}
                        onChange={(v) => field.onChange(v ?? '')}
                        onBlur={field.onBlur}
                        className="dkn-phone"
                        // Custom searchable country picker (flag + "Norway (+47)"),
                        // built on react-select. It renders flags locally, so the
                        // library never falls back to its third-party flag CDN.
                        countrySelectComponent={PhoneCountrySelect}
                        numberInputProps={{
                            id: name,
                            className: `form-control${error ? ' is-invalid' : ''}`,
                            'aria-invalid': error ? true : undefined,
                            'aria-describedby': describedBy,
                        }}
                    />
                )}
            />
            {helpText && !error && (
                <Form.Text id={helpId} className="text-muted">
                    {helpText}
                </Form.Text>
            )}
            {message && (
                <div className="invalid-feedback d-block" id={errorId}>
                    {message}
                </div>
            )}
        </Form.Group>
    );
}
